import { useEffect, useState } from "react";
import { Tags, Plus, Trash2, Save, AlertCircle, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Owner-only industry taxonomy editor. Lives in Settings.
 * Backed by /api/industries (GET) + PUT for owner/admin.
 *
 * The user can rename labels, change keys (snake_cased), add new rows,
 * and delete unused industries. Save persists the full list.
 */
export default function IndustryTaxonomyCard() {
  const { user, hasPermission } = useAuth();
  const allowed =
    user?.role === "owner" ||
    user?.role === "admin" ||
    (typeof hasPermission === "function" && hasPermission("data.edit_industry_taxonomy"));

  const [items, setItems] = useState([]);
  const [breakdown, setBreakdown] = useState({ customers: [], leads: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    try {
      const [t, b] = await Promise.all([
        api.get("/industries"),
        api.get("/industries/breakdown").catch(() => ({ data: { customers: [], leads: [] } })),
      ]);
      setItems(t.data?.industries || []);
      setBreakdown(b.data || { customers: [], leads: [] });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load industries"));
    }
  };
  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!allowed) return null;

  const update = (i, patch) => {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const add = () => {
    setItems((arr) => [...arr, { key: "", label: "" }]);
  };
  const remove = (i) => {
    setItems((arr) => arr.filter((_, idx) => idx !== i));
  };

  const usageFor = (key) => {
    const c = (breakdown.customers || []).find((x) => x.industry === key)?.count || 0;
    const l = (breakdown.leads || []).find((x) => x.industry === key)?.count || 0;
    return { c, l };
  };

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const clean = items
        .map((i) => ({
          key: (i.key || "").trim().toLowerCase().replace(/\s+/g, "_"),
          label: (i.label || "").trim() || (i.key || "").replace(/_/g, " "),
        }))
        .filter((i) => i.key);
      const r = await api.put("/industries", { industries: clean });
      setItems(r.data?.industries || clean);
      setSavedAt(new Date().toISOString());
      setTimeout(() => setSavedAt(null), 4000);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally { setBusy(false); }
  };

  return (
    <div className="hv-card p-5" data-testid="industry-taxonomy-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-[#A78BFA]/15 border border-[#A78BFA]/40 flex items-center justify-center">
          <Tags className="w-5 h-5 text-[#A78BFA]" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-2xl font-bold">Industry taxonomy</h2>
          <p className="text-xs text-[#a0aabf]">
            Edit the list of industries available across customers, leads and suspects. Owner-only.
          </p>
        </div>
        {savedAt && <span className="text-xs text-emerald-300">Saved ✓</span>}
      </div>

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 rounded-md p-2 text-xs text-rose-300 mb-3 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <div className="space-y-1.5">
        {items.map((it, i) => {
          const usage = usageFor(it.key);
          return (
            <div key={i} className="grid grid-cols-[140px_1fr_auto_auto] items-center gap-2" data-testid={`industry-row-${i}`}>
              <input
                value={it.key}
                onChange={(e) => update(i, { key: e.target.value })}
                placeholder="key (snake_case)"
                className="hv-input text-xs font-mono"
                data-testid={`industry-key-${i}`}
              />
              <input
                value={it.label}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder="Display label"
                className="hv-input text-xs"
                data-testid={`industry-label-${i}`}
              />
              <span className="text-[10px] text-[#647087] font-mono whitespace-nowrap" title="customers / leads using this industry">
                {usage.c}c · {usage.l}l
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="hv-btn hv-btn-ghost text-xs"
                title="Remove industry"
                data-testid={`industry-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-300" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={add}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="industry-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add industry
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="industry-save-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {busy ? "Saving…" : "Save taxonomy"}
        </button>
      </div>
    </div>
  );
}
